import { lazy, Suspense, useEffect, type ComponentType } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { PwaControls } from "@/components/PwaControls";
import { ProtectedRoute, RoleRoute } from "@/components/RouteGuards";
import { listingReviewDestination } from "@/lib/notificationRouting";

function lazyPage<Props extends object = Record<string, never>>(
  loader: () => Promise<Record<string, unknown>>,
  exportName: string,
) {
  return lazy(async () => ({
    default: (await loader())[exportName] as ComponentType<Props>,
  }));
}

const authPages = () => import("@/pages/AuthPages");
const accountPages = () => import("@/pages/AccountPages");
const adminPages = () => import("@/pages/AdminPages");
const auctionPages = () => import("@/pages/AuctionPages");
const marketplacePages = () => import("@/pages/MarketplacePages");
const orderingPages = () => import("@/pages/OrderingPages");

const HomePage = lazyPage(() => import("@/pages/HomePage"), "HomePage");
const AiChatPage = lazyPage(() => import("@/pages/AiChatPage"), "AiChatPage");
const LoginPage = lazyPage(authPages, "LoginPage");
const SignupPage = lazyPage(authPages, "SignupPage");
const ForgotPasswordPage = lazyPage(authPages, "ForgotPasswordPage");
const ResetPasswordPage = lazyPage(authPages, "ResetPasswordPage");
const ProfilePage = lazyPage<{ initialEditing?: boolean }>(
  accountPages,
  "ProfilePage",
);
const NotificationsPage = lazyPage(accountPages, "NotificationsPage");
const AgentGroupsPage = lazyPage(accountPages, "AgentGroupsPage");
const MyGroupsPage = lazyPage(accountPages, "MyGroupsPage");
const GroupDetailPage = lazyPage<{ farmerView?: boolean }>(
  accountPages,
  "GroupDetailPage",
);
const AdminDashboardPage = lazyPage(adminPages, "AdminDashboardPage");
const BulkUploadPage = lazyPage(adminPages, "BulkUploadPage");
const InvitationsPage = lazyPage(adminPages, "InvitationsPage");
const ReportsPage = lazyPage(adminPages, "ReportsPage");
const SoldListingsPage = lazyPage(adminPages, "SoldListingsPage");
const UserManagementPage = lazyPage<{ initialAction?: "create" }>(
  adminPages,
  "UserManagementPage",
);
const AssignAgentPage = lazyPage(adminPages, "AssignAgentPage");
const AuctionsPage = lazyPage(auctionPages, "AuctionsPage");
const AuctionDetailPage = lazyPage(auctionPages, "AuctionDetailPage");
const BidHistoryPage = lazyPage(auctionPages, "BidHistoryPage");
const AuctionManagementPage = lazyPage(auctionPages, "AuctionManagementPage");
const AuctionNotificationsPage = lazyPage(
  auctionPages,
  "AuctionNotificationsPage",
);
const ListingsPage = lazyPage(marketplacePages, "ListingsPage");
const ListingDetailPage = lazyPage(marketplacePages, "ListingDetailPage");
const ListingEditorPage = lazyPage(marketplacePages, "ListingEditorPage");
const ListingPreviewPage = lazyPage(marketplacePages, "ListingPreviewPage");
const MyListingsPage = lazyPage(marketplacePages, "MyListingsPage");
const DraftsPage = lazyPage(marketplacePages, "DraftsPage");
const WatchlistPage = lazyPage(marketplacePages, "WatchlistPage");
const OrderingDashboardPage = lazyPage(orderingPages, "OrderingDashboardPage");
const SelectCustomerPage = lazyPage(orderingPages, "SelectCustomerPage");
const CustomerDetailPage = lazyPage(orderingPages, "CustomerDetailPage");
const AddProductsPage = lazyPage(orderingPages, "AddProductsPage");
const MixingPage = lazyPage(orderingPages, "MixingPage");
const DispatchPage = lazyPage(orderingPages, "DispatchPage");
const DeliveryPage = lazyPage(orderingPages, "DeliveryPage");
const ReviewOrderPage = lazyPage(orderingPages, "ReviewOrderPage");
const OrderSummaryPage = lazyPage(orderingPages, "OrderSummaryPage");
const OrderQueuePage = lazyPage(orderingPages, "OrderQueuePage");
const OrderDetailsPage = lazyPage(orderingPages, "OrderDetailsPage");

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);
  return null;
}

function NotFoundPage() {
  return (
    <div className="not-found">
      <span>404</span>
      <h1>That page is out in the paddock.</h1>
      <p>The link may be old, or the page may have moved.</p>
      <a className="button button--primary" href="/">
        Return home
      </a>
    </div>
  );
}

function QueryParamRedirect({
  names,
  target,
  fallback,
}: {
  names: string[];
  target: (value: string) => string;
  fallback: string;
}) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const value = names.map((name) => params.get(name)).find(Boolean);
  return <Navigate to={value ? target(value) : fallback} replace />;
}

function ListingReviewRedirect() {
  const { id } = useParams();
  return (
    <Navigate
      to={id ? listingReviewDestination(id) : "/notifications"}
      replace
    />
  );
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <PwaControls />
      <Suspense
        fallback={
          <div className="route-loading" role="status">
            <span className="spinner" /> Loading Carrfields…
          </div>
        }
      >
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/register" element={<Navigate to="/signup" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route element={<AppShell />}>
            <Route index element={<HomePage />} />
            <Route path="ai-chat" element={<AiChatPage />} />
            <Route path="search" element={<AiChatPage />} />
            <Route path="ai-search" element={<AiChatPage />} />
            <Route path="listings" element={<ListingsPage />} />
            <Route path="listing/:id" element={<ListingDetailPage />} />
            <Route path="auctions" element={<AuctionsPage />} />
            <Route path="auction/:id" element={<AuctionDetailPage />} />
            <Route
              path="auction/:id/bid-history"
              element={<BidHistoryPage />}
            />

            <Route element={<ProtectedRoute />}>
              <Route path="profile" element={<ProfilePage />} />
              <Route
                path="edit-profile"
                element={<ProfilePage initialEditing />}
              />
              <Route path="watchlist" element={<WatchlistPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route
                path="admin/notificationCenter"
                element={<Navigate to="/notifications" replace />}
              />
              <Route path="my-groups" element={<MyGroupsPage />} />
              <Route
                path="my-groups/:id"
                element={<Navigate to="/my-groups" replace />}
              />

              <Route element={<RoleRoute roles={["agent", "admin"]} />}>
                <Route path="listings/new" element={<ListingEditorPage />} />
                <Route
                  path="listings/:id/edit"
                  element={<ListingEditorPage />}
                />
                <Route
                  path="edit-listing/:id"
                  element={<ListingEditorPage />}
                />
                <Route
                  path="listings/preview"
                  element={<ListingPreviewPage />}
                />
                <Route path="previewListing" element={<ListingPreviewPage />} />
                <Route path="my-listings" element={<MyListingsPage />} />
                <Route path="drafts" element={<DraftsPage />} />
                <Route path="groups" element={<AgentGroupsPage />} />
                <Route path="groups/:id" element={<GroupDetailPage />} />
                <Route path="admin/invitations" element={<InvitationsPage />} />
                <Route
                  path="admin/send-invitation"
                  element={<InvitationsPage />}
                />
                <Route
                  path="admin/addlisting"
                  element={<Navigate to="/listings/new" replace />}
                />
                <Route
                  path="admin/mylistings"
                  element={<Navigate to="/my-listings" replace />}
                />
                <Route
                  path="agent/groups"
                  element={<Navigate to="/groups" replace />}
                />
                <Route
                  path="agent/groups/create-group"
                  element={<Navigate to="/groups?create=1" replace />}
                />
                <Route
                  path="agent/groups/add-farmers"
                  element={
                    <QueryParamRedirect
                      names={["groupId"]}
                      target={(id) => `/groups/${encodeURIComponent(id)}?add=1`}
                      fallback="/groups"
                    />
                  }
                />
                <Route path="agent/groups/:id" element={<GroupDetailPage />} />
              </Route>

              <Route element={<RoleRoute roles={["admin"]} />}>
                <Route path="admin" element={<AdminDashboardPage />} />
                <Route path="admin/users" element={<UserManagementPage />} />
                <Route
                  path="admin/adminDashboard"
                  element={<Navigate to="/admin" replace />}
                />
                <Route
                  path="admin/manageUsers"
                  element={<Navigate to="/admin/users" replace />}
                />
                <Route
                  path="admin/assign-agent"
                  element={<AssignAgentPage />}
                />
                <Route path="admin/assignAgent" element={<AssignAgentPage />} />
                <Route
                  path="admin/create-agent"
                  element={<UserManagementPage initialAction="create" />}
                />
                <Route path="admin/bulk-upload" element={<BulkUploadPage />} />
                <Route
                  path="admin/bulk-upload-farmers"
                  element={<Navigate to="/admin/bulk-upload" replace />}
                />
                <Route path="admin/reports" element={<ReportsPage />} />
                <Route
                  path="admin/farmerAgentReport"
                  element={<Navigate to="/admin/reports" replace />}
                />
                <Route
                  path="admin/sold-listings"
                  element={<SoldListingsPage />}
                />
                <Route
                  path="admin/soldListings"
                  element={<Navigate to="/admin/sold-listings" replace />}
                />
                <Route
                  path="admin/listings/:id/review"
                  element={<ListingReviewRedirect />}
                />
                <Route
                  path="admin/review-listing/:id"
                  element={<ListingReviewRedirect />}
                />
                <Route
                  path="admin/auctions"
                  element={<AuctionManagementPage />}
                />
                <Route
                  path="admin/auctions/create"
                  element={<AuctionManagementPage />}
                />
                <Route
                  path="admin/auctions/:id/edit"
                  element={<AuctionManagementPage />}
                />
                <Route
                  path="admin/auctions/notifications"
                  element={<AuctionNotificationsPage />}
                />
                <Route
                  path="admin/auction-management"
                  element={<Navigate to="/admin/auctions" replace />}
                />
                <Route
                  path="admin/auction-management/create-auction"
                  element={<Navigate to="/admin/auctions/create" replace />}
                />
                <Route
                  path="admin/auction-management/edit-auction"
                  element={
                    <QueryParamRedirect
                      names={["id", "auctionId"]}
                      target={(id) =>
                        `/admin/auctions/${encodeURIComponent(id)}/edit`
                      }
                      fallback="/admin/auctions"
                    />
                  }
                />
                <Route
                  path="admin/auction-management/notifications"
                  element={<AuctionNotificationsPage />}
                />
                <Route
                  path="admin/auction-management/view/:id"
                  element={<AuctionDetailPage />}
                />
              </Route>

              <Route
                element={
                  <RoleRoute
                    roles={["admin", "agronomist_admin", "agronomist_agent"]}
                  />
                }
              >
                <Route path="ordering" element={<OrderingDashboardPage />} />
                <Route
                  path="ordering/customers"
                  element={<SelectCustomerPage />}
                />
                <Route
                  path="ordering/select-customer"
                  element={<Navigate to="/ordering/customers" replace />}
                />
                <Route
                  path="ordering/customer/:customerNo"
                  element={<CustomerDetailPage />}
                />
                <Route
                  path="ordering/customer-detail"
                  element={
                    <QueryParamRedirect
                      names={["customerId", "customerNo"]}
                      target={(id) =>
                        `/ordering/customer/${encodeURIComponent(id)}`
                      }
                      fallback="/ordering/customers"
                    />
                  }
                />
                <Route path="ordering/products" element={<AddProductsPage />} />
                <Route
                  path="ordering/add-products"
                  element={<Navigate to="/ordering/products" replace />}
                />
                <Route path="ordering/mixing" element={<MixingPage />} />
                <Route
                  path="ordering/mixing-instructions"
                  element={<Navigate to="/ordering/mixing" replace />}
                />
                <Route path="ordering/dispatch" element={<DispatchPage />} />
                <Route
                  path="ordering/dispatch-location"
                  element={<Navigate to="/ordering/dispatch" replace />}
                />
                <Route path="ordering/delivery" element={<DeliveryPage />} />
                <Route path="ordering/review" element={<ReviewOrderPage />} />
                <Route path="ordering/summary" element={<OrderSummaryPage />} />
                <Route path="ordering/queue" element={<OrderQueuePage />} />
                <Route
                  path="ordering/orders/:orderId"
                  element={<OrderDetailsPage />}
                />
                <Route
                  path="ordering/order-details/:orderId"
                  element={<OrderDetailsPage />}
                />
              </Route>
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}
